import React, { useState } from "react";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Renders a sticky bulk-delete bar that shows selection count and a Delete Selected button.
 * Renders nothing when selectedIds is empty.
 *
 * Props:
 *  - resource: resource segment for the bulk-delete endpoint (e.g. "purchase-orders")
 *  - selectedIds: string[]
 *  - onClear: () => void
 *  - onDeleted: () => void          // called after successful delete
 *  - testid?: string
 */
export default function BulkDeleteBar({ resource, selectedIds, onClear, onDeleted, testid }) {
  const [busy, setBusy] = useState(false);
  if (!selectedIds || selectedIds.length === 0) return null;

  const run = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} selected entr${selectedIds.length === 1 ? "y" : "ies"}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${resource}/bulk-delete`, { ids: selectedIds });
      toast.success(`Deleted ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}`);
      onClear?.();
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#1D2951] text-white rounded-md px-4 py-2 flex items-center justify-between shadow-md" data-testid={testid || "bulk-delete-bar"}>
      <div className="text-sm font-semibold tracking-tight">
        {selectedIds.length} selected
      </div>
      <div className="flex gap-2">
        <button
          onClick={onClear}
          className="text-xs px-3 py-1.5 rounded-md hover:bg-white/10 inline-flex items-center gap-1"
          data-testid={`${testid || "bulk-delete"}-clear`}
        >
          <X size={12} /> Clear
        </button>
        <button
          onClick={run}
          disabled={busy}
          className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1"
          data-testid={`${testid || "bulk-delete"}-confirm`}
        >
          <Trash2 size={12} /> {busy ? "Deleting…" : "Delete Selected"}
        </button>
      </div>
    </div>
  );
}
